import React from 'react';
import { WeatherData, AirQualityData, MarineData } from '../types';

interface WeatherCardProps {
  weather: WeatherData | null;
  aqi?: AirQualityData | null;
  marine?: MarineData | null;
  loading?: boolean;
}

const WeatherCard: React.FC<WeatherCardProps> = ({ weather, aqi, marine, loading }) => {
  if (loading) {
    // ... skeleton
    return (
      <div className="bg-white rounded-[2rem] p-6 shadow-sm border border-gray-100 animate-pulse w-full">
        <div className="flex justify-between items-start mb-6">
          <div className="space-y-2">
            <div className="w-32 h-6 bg-gray-200 rounded-full"></div>
            <div className="w-24 h-4 bg-gray-100 rounded-full"></div>
          </div>
          <div className="w-16 h-8 bg-gray-200 rounded-full"></div>
        </div>
        {/* ... */}
      </div>
    );
  }

  if (!weather) return null;

  // Dynamic gradient based on weather condition
  const getGradient = (condition: string) => {
    const c = condition.toLowerCase();
    if (c.includes('hujan') || c.includes('rain')) return 'from-blue-600 to-indigo-800';
    if (c.includes('cerah') || c.includes('sun')) return 'from-orange-400 to-amber-600';
    if (c.includes('awan') || c.includes('cloud')) return 'from-blue-400 to-blue-600';
    return 'from-blue-500 to-blue-700';
  };

  const gradientClass = getGradient(weather.condition);

  return (
    <div className={`overflow-hidden relative p-6 w-full text-white bg-gradient-to-br shadow-xl transition-all duration-500 ${gradientClass} rounded-[2rem] group hover:shadow-2xl`}>
      {/* Background decorations */}
      <div className="absolute top-0 right-0 -mt-16 -mr-16 w-48 h-48 bg-white rounded-full opacity-10 blur-3xl transition-opacity duration-700 group-hover:opacity-20"></div>
      <div className="absolute bottom-0 left-0 -mb-10 -ml-10 w-32 h-32 bg-black rounded-full opacity-10 blur-2xl"></div>

      <div className="relative z-10">
        <div className="flex justify-between items-start mb-6">
          <div>
            <div className="flex gap-2 items-center mb-1">
              <span className="text-xl">📍</span>
              <h3 className="text-lg font-bold leading-none">{weather.location || 'Tegal'}</h3>
            </div>
            <p className="pl-7 text-xs font-medium text-white/80">
              {new Date().toLocaleDateString('id-ID', { weekday: 'long', day: 'numeric', month: 'short' })}
            </p>
          </div>
        </div>

        <div className="flex gap-6 items-center mb-8">
          <span className="text-6xl filter drop-shadow-lg transition-transform duration-500 transform group-hover:scale-110">
            {weather.icon}
          </span>
          <div>
            <div className="mb-1 text-5xl font-black tracking-tighter">{weather.temp}</div>
            <div className="inline-block px-3 py-1 text-sm font-medium capitalize rounded-lg backdrop-blur-sm text-white/90 bg-white/10">
              {weather.condition}
            </div>
          </div>
        </div>

        <div className="grid grid-cols-3 gap-3">
          <div className="p-3 text-center rounded-2xl border backdrop-blur-md transition-colors bg-white/10 border-white/10 hover:bg-white/20">
            <div className="text-[10px] text-white/70 uppercase tracking-wider mb-1">Kelembapan</div>
            <div className="text-sm font-bold">{weather.humidity}</div>
          </div>
          <div className="p-3 text-center rounded-2xl border backdrop-blur-md transition-colors bg-white/10 border-white/10 hover:bg-white/20">
            <div className="text-[10px] text-white/70 uppercase tracking-wider mb-1">Hujan</div>
            <div className="text-sm font-bold">{weather.rainChance}</div>
          </div>
          <div className="p-3 text-center rounded-2xl border backdrop-blur-md transition-colors bg-white/10 border-white/10 hover:bg-white/20">
            <div className="text-[10px] text-white/70 uppercase tracking-wider mb-1">Angin</div>
            <div className="text-sm font-bold">{weather.windSpeed || '-'}</div>
          </div>
        </div>

        {/* Extra Info: AQI & Marine */}
        {(aqi || marine) && (
          <div className="grid grid-cols-2 gap-3 pt-4 mt-4 border-t border-white/10">
            {aqi && (
              <div className="flex gap-2 items-center">
                <div className={`w-2 h-2 rounded-full ${aqi.status === 'Baik' ? 'bg-green-400' : aqi.status === 'Sedang' ? 'bg-yellow-400' : 'bg-red-400'}`}></div>
                <div className="text-xs">
                  <span className="opacity-70">Udara: </span>
                  <span className="font-semibold">{aqi.status} ({aqi.aqi})</span>
                </div>
              </div>
            )}
            {marine && (
              <div className="flex gap-2 items-center">
                <div className="text-lg">🌊</div>
                <div className="text-xs">
                  <span className="opacity-70">Ombak: </span>
                  <span className={`font-semibold ${marine.isRobPotential ? 'text-red-300 animate-pulse' : ''}`}>
                    {marine.waveHeight}m {marine.isRobPotential ? '⚠️' : ''}
                  </span>
                </div>
              </div>
            )}
          </div>
        )}

        <div className="mt-3 text-right">
          <a 
            href="https://open-meteo.com/" 
            target="_blank" 
            rel="noopener noreferrer" 
            className="text-[10px] text-white/50 hover:text-white transition-colors border-b border-transparent hover:border-white/50"
          >
            Weather Data Powered by Open-Meteo.com
          </a>
        </div>
      </div>
    </div>
  );
};

export default WeatherCard;
